import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Grid,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  CircularProgress
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/authUtils';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { useSnackbar } from '../CentralSnackbar';

const TenantDialog = ({ open, onClose, onSave, tenant }) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const [formData, setFormData] = useState({
    bezeichnung: '',
    hl7_name: '',
    is_active: true,
  });
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  const isEditMode = tenant !== null && tenant !== undefined;

  // Load all users
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${getApiBaseUrl()}/users/`, {
        headers: getAuthHeaders(),
      });
      setUsers(response.data);
      return response.data; // Return users for further processing
    } catch (error) {
      console.error('Error loading users:', error);
      showError(t('admin.tenantManagement.loadUsersError'), 'ERR-TENANTDLG-001');
      return [];
    } finally {
      setLoadingUsers(false);
    }
  };

  // Update selected users based on current tenant assignments
  const updateSelectedUsersForTenant = (users, currentTenant) => {
    if (isEditMode && currentTenant) {
      const tenantUsers = users.filter(user => {
        if (!user.tenant) return false;
        const userTenants = user.tenant.split(',').map(t => t.trim());
        return userTenants.includes(String(currentTenant.id));
      });
      const selectedUserIds = tenantUsers.map(user => user.id);
      setSelectedUsers(selectedUserIds);
    } else {
      setSelectedUsers([]);
    }
  };

  // Handle user selection changes
  const handleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!userSearchTerm) return true;
    const searchLower = userSearchTerm.toLowerCase();
    const fullName = `${user.academic_degree || ''} ${user.full_name || user.username}`.trim().toLowerCase();
    const username = user.username.toLowerCase();
    return fullName.includes(searchLower) || username.includes(searchLower);
  });

  useEffect(() => {
    if (open) {
      // Load users when dialog opens
      const initializeDialog = async () => {
        const users = await loadUsers();
        
        if (isEditMode) {
          setFormData({
            bezeichnung: tenant.bezeichnung || '',
            hl7_name: tenant.hl7_name || '',
            is_active: tenant.is_active !== undefined ? tenant.is_active : true,
          });
          // Update selected users after users are loaded
          updateSelectedUsersForTenant(users, tenant);
        } else {
          // Reset für neuen Mandanten
          setFormData({
            bezeichnung: '',
            hl7_name: '',
            is_active: true,
          });
          setSelectedUsers([]); // Reset user selection for new tenant
        }
        setErrors({}); // Fehler zurücksetzen, wenn der Dialog geöffnet wird
        setUserSearchTerm(''); // Reset user search when dialog opens
      };
      
      initializeDialog();
    }
  }, [tenant, open, isEditMode]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' || type === 'switch' ? checked : value,
    }));
  };
  
  const validate = () => {
    const newErrors = {};
    if (!formData.bezeichnung.trim()) {
      newErrors.bezeichnung = 'Dieses Feld ist erforderlich';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }
    
    const tenantData = {
      ...formData,
      id: isEditMode ? tenant.id : undefined,
      selectedUsers: selectedUsers,
    };
    
    onSave(tenantData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { minWidth: '800px', maxWidth: '1200px' } }}>
      <DialogTitle>
        {isEditMode ? 'Mandant bearbeiten' : 'Mandant hinzufügen'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Left Column: Tenant Details */}
          <Grid item xs={12} md={5}>
            <Typography variant="h6" gutterBottom>
              Mandant-Details
            </Typography>
            <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                autoFocus
                margin="dense"
                name="bezeichnung"
                label="Bezeichnung"
                type="text"
                variant="outlined"
                value={formData.bezeichnung}
                onChange={handleChange}
                required
                error={!!errors.bezeichnung}
                helperText={errors.bezeichnung || 'z.B. Klinik Musterstadt bzw. MVZ Musterstadt'}
                fullWidth
              />
              <TextField
                margin="dense"
                name="hl7_name"
                label="HL7 Name"
                type="text"
                variant="outlined"
                value={formData.hl7_name}
                onChange={handleChange}
                helperText="Kennung des Mandanten aus HL7-Systemnachricht"
                fullWidth
              />
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={formData.is_active} 
                      onChange={handleChange} 
                      name="is_active" 
                    />
                  }
                  label="Mandant ist aktiv"
                />
              </Box>
            </Box>
          </Grid>

          {/* Right Column: User Selection */}
          <Grid item xs={12} md={7}>
            <Typography variant="h6" gutterBottom>
              Berechtigte Benutzer
            </Typography>
            <TextField
              placeholder={t('admin.tenantManagement.search_users_placeholder')}
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ marginRight: 1, color: 'text.secondary' }} />
              }}
            />
            <Paper variant="outlined" sx={{ height: 300, overflow: 'auto', p: 1, minWidth: '450px' }}>
              {loadingUsers ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress size={40} />
                </Box>
              ) : (
                <List dense>
                  {filteredUsers.map((user) => (
                    <ListItem key={user.id} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Checkbox
                          edge="start"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleUserSelection(user.id)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={`${user.academic_degree || ''} ${user.full_name || user.username}`.trim()}
                        secondary={user.job_title || ''}
                        secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {selectedUsers.length} von {filteredUsers.length} Benutzern ausgewählt
              {userSearchTerm && ` (${users.length} gesamt)`}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} className="button-cancel">
          Abbrechen
        </Button>
        <Button onClick={handleSave} variant="contained" className="button">
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantDialog; 